import React, { useEffect, useState, useRef, useCallback } from 'react';
import './Menu.scss';
import { RMModal } from 'components/modal';
import { CreateNewTreeNodeDTO } from 'business/models/Models';
import { ECreateNodeType } from './ProjectNode';
import { TreeService } from 'business/services/TreeService';
import { showSnackBar, MaxNameLength } from 'utils/Global';
import ETreeNodeTypes from 'enums/ETreeNodeTypes';
import { FmlxProgressBar } from 'fmlx-common-ui';

export type CreateNodeTypeModalProps = {
  type: ECreateNodeType;
  parentTreeNodeId: any;
  newNode: (newNode, parentTreeNode) => void;
  suggestedName?: string;
  newFolderType?: any;
  setType: (val) => void;
};

const treeService = new TreeService();

export default function TreeNodeCreator(props: CreateNodeTypeModalProps) {
  const [newName, setNewName] = useState('');
  const [modalVisibility, setModalVisibility] = useState(false);
  const [okayDisabled, setOkayDisabled] = useState<boolean>(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const getNodeType = () => {
    switch (props.type) {
      case ECreateNodeType.ProjectFolder:
        return ETreeNodeTypes.ProjectsFolder;
      case ECreateNodeType.SynchrotronTripv4Folder:
        return ETreeNodeTypes.SynchrotronTripv4Folder;
      case ECreateNodeType.SearchesFolder:
        return ETreeNodeTypes.SearchesFolder;
      default:
        return ETreeNodeTypes.Project;
    }
  };

  const setUniqueName = useCallback(
    async (signal?: AbortSignal) => {
      const nodeType = getNodeType();
      try {
        await treeService
          .getNewUniqueName(props.parentTreeNodeId, nodeType, '', signal)
          .then((data) => {
            setNewName((prev) => (prev.trim() === '' ? data.name : prev));
          });
      } catch (error) {
        console.error('Fetch new name failed!', error);
      }
    },
    [props.parentTreeNodeId, props.type],
  );

  useEffect(() => {
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const fetchUniqueName = !props.suggestedName && newName.trim() === '';

    if (props.type !== ECreateNodeType.None) {
      if (fetchUniqueName) {
        setUniqueName(controller.signal);
      }
      setModalVisibility(true);
    } else {
      setModalVisibility(false);
    }

    return () => {
      controller.abort();
      abortControllerRef.current = null;
    };
  }, [props.type, setUniqueName]);

  useEffect(() => {
    if (!modalVisibility) {
      props.setType(ECreateNodeType.None);
      setNewName('');
    }
  }, [modalVisibility]);

  function getBody() {
    const title = props.type.includes('Folder') ? 'Folder Name' : 'Project Name';
    return (
      <>
        {title}:
        <br id="createnodemodal-getbody-breakline" />
        <input
          id="createnodemodal-getbody-title-input"
          type="text"
          value={newName}
          onChange={(e) => {
            setNewName(e.target.value);
          }}
          className="input-borderless"
          autoFocus
          readOnly={okayDisabled}
          onKeyDown={() => {
            abortControllerRef.current?.abort();
          }}
        />
        {okayDisabled && (
          <span id="createnodemodal-getbody-progressbar" style={{ float: 'right' }}>
            <FmlxProgressBar
              type={FmlxProgressBar.Type.CIRCULAR}
              variant={FmlxProgressBar.Variant.INDETERMINATE}
              size={FmlxProgressBar.Size.SMALL}
            />
          </span>
        )}
      </>
    );
  }
  function getHeading() {
    if (props.type === ECreateNodeType.Project) {
      return 'Create Project';
    } else if (props.type.includes('Folder')) {
      return 'Create Folder';
    }
  }
  async function createItem() {
    if (newName.length == 0) {
      showSnackBar('Name can not be empty.');
      return;
    } else if (newName.length > MaxNameLength.TREE_NODE) {
      const nodeTypeStr = props.type === ECreateNodeType.Project ? 'project' : 'folder';
      showSnackBar(
        `Please make sure the ${nodeTypeStr} name is no more than ${MaxNameLength.TREE_NODE} characters.`,
      );
      return;
    }
    setOkayDisabled(true);
    let newNode: CreateNewTreeNodeDTO = new CreateNewTreeNodeDTO();
    newNode.name = newName;
    newNode.parentId = props.parentTreeNodeId;
    if (props.newFolderType && props.type !== ECreateNodeType.Project) {
      newNode.type = props.newFolderType;
    } else {
      newNode.type =
        props.type === ECreateNodeType.ProjectFolder
          ? ECreateNodeType.ProjectFolder
          : ECreateNodeType.Project;
    }
    const ser: TreeService = new TreeService();
    newNode = await ser.createNewTreeNode(newNode);
    if (newNode === null) {
      return null;
    }
    if (newNode.isAlreadyExist) {
      showSnackBar(`Cannot create ${newNode.type}: Node name already exist!`);
      setOkayDisabled(false);
      return;
    }
    await props.newNode(newNode, props.parentTreeNodeId);
    setOkayDisabled(false);
    setModalVisibility(false);
  }
  return (
    <RMModal
      id="createnodemodal-modal-container"
      showModal={modalVisibility}
      setShowModal={setModalVisibility}
      onOk={() => createItem()}
      body={getBody()}
      heading={getHeading()}
      okButtonText={'Ok'}
      noButtonText={'Cancel'}
      okayDisabled={okayDisabled}
    />
  );
}
